import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCart } from '~/hooks/cart';
import { useCategories } from '~/hooks/categories';
import { useSettings } from '~/hooks/settings';
import IComplement from '~/interfaces/IComplement';
import IComplementItem from '~/interfaces/IComplementItem';
import IProduct from '~/interfaces/IProduct';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import ICartComplement from '~/interfaces/ICartComplement';

const groupItemsByTitle = (items: IComplementItem[], groupOrders?: Record<string, number>) => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  const grouped = items.reduce((acc, item) => {
    const groupKey = item.groupTitle || 'Outros';
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(item);
    return acc;
  }, {} as Record<string, IComplementItem[]>);

  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    if (a === 'Outros') return 1;
    if (b === 'Outros') return -1;
    
    const orderA = groupOrders?.[a] ?? 0;
    const orderB = groupOrders?.[b] ?? 0;
    
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    
    return a.localeCompare(b);
  });

  return sortedGroups;
};

interface AddProductProps {
  product: IProduct;
  onClose(): void;
}

interface ISelectComplement {
  [key: string]: ICartComplement;
}

const { REACT_APP_ORDER_COMPLEMENTS } = process.env;

const AddProduct: React.FC<AddProductProps> = ({ product, onClose }) => {
  const { add } = useCart();
  const { company } = useSettings();
  const { complements, categories } = useCategories();
  const [quantity, setQuantity] = useState(1);
  const [observations, setObservations] = useState('');
  const [selectedComplements, setSelectedComplements] =
    useState<ISelectComplement>({} as ISelectComplement);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    setQuantity(1);
    setObservations('');
    setSelectedComplements({});
  }, [product]);

  const listComplements = useMemo(() => {
    if (!product || !product.category) return [];

    const findCategory = categories.find(
      find => find.uid === product.category.uid,
    );

    if (!findCategory || !findCategory.complements) return [];

    const filtered = complements.filter(item => {
      item.items.forEach(element => {
        element.value_formatted = getCurrencyFormatted(
          parseFloat(element.value),
        );
      });

      return findCategory.complements.includes(item.uid);
    });

    return filtered;
  }, [product, complements, categories]);

  const allComplements = useMemo(() => {
    if (!product.groups_complements) {
      const res = [...listComplements];
      res.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));

      if (REACT_APP_ORDER_COMPLEMENTS) {
        for (let index = 0; index < res.length; index++) {
          res[index].items = res[index].items.sort((a, b) =>
            parseFloat(a.value) > parseFloat(b.value)
              ? 1
              : parseFloat(b.value) > parseFloat(a.value)
              ? -1
              : 0,
          );
        }
      }

      return res;
    }

    const filtered = complements.filter(item => {
      item.items.forEach(element => {
        element.value_formatted = getCurrencyFormatted(
          parseFloat(element.value),
        );
      });

      return product.groups_complements.includes(item.uid);
    });

    const res = [...listComplements, ...filtered];
    res.sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));

    if (REACT_APP_ORDER_COMPLEMENTS) {
      for (let index = 0; index < res.length; index++) {
        res[index].items = res[index].items.sort((a, b) =>
          parseFloat(a.value) > parseFloat(b.value)
            ? 1
            : parseFloat(b.value) > parseFloat(a.value)
            ? -1
            : 0,
        );
      }
    }

    return res;
  }, [listComplements, product, complements]);

  const productValue = useMemo(() => {
    let total = 0;

    // Check if any selected complement belongs to a pizza-type group
    const hasPizzaTypeComplement = Object.values(selectedComplements).some(value => {
      const complementGroup = allComplements.find(group => group.uid === value.group);
      return complementGroup?.pizzaType;
    });

    if (hasPizzaTypeComplement) {
      // For pizza-type groups, use the highest value among selected items
      Object.values(selectedComplements).forEach(value => {
        const complementGroup = allComplements.find(group => group.uid === value.group);
        if (complementGroup?.pizzaType) {
          const itemValue = parseFloat(value.item.value);
          if (itemValue > total) total = itemValue;
        } else {
          // For non-pizza groups, add normally
          total += parseFloat(value.item.value) * value.quantity;
        }
      });
    } else {
      // Normal calculation for non-pizza products
      Object.values(selectedComplements).forEach(value => {
        total += parseFloat(value.item.value) * value.quantity;
      });
    }

    return getCurrencyFormatted(total);
  }, [selectedComplements, allComplements]);

  const handleClickPlus = useCallback(() => {
    setQuantity(qtd => qtd + 1);
  }, []);

  const handleClickMinus = useCallback(() => {
    const qtd = quantity - 1;
    if (qtd >= 1) setQuantity(qtd);
  }, [quantity]);

  const handleClickPlusKg = useCallback(() => {
    setQuantity(qtd => qtd + 0.1);
  }, []);

  const handleClickMinusKg = useCallback(() => {
    const qtd = quantity - 0.1;
    if (qtd >= 0.1) setQuantity(qtd);
  }, [quantity]);

  const handleClickAdd = useCallback(async () => {
    let errorDescription = '';
    const cartComplements = [] as ICartComplement[];
    setErrors({});

    if (allComplements.length > 0) {
      const totals: any = {};
      Object.values(selectedComplements).forEach(value => {
        if (!totals[value.group]) totals[value.group] = 0;

        totals[value.group] += 1;

        cartComplements.push(value);
      });

      let tmpErrors: any = {};
      allComplements.forEach(item => {
        // obrigatorio
        if (item.type === '1') {
          if (totals[item.uid] < item.min || !totals[item.uid]) {
            errorDescription = 'Verifique a quantidade mínima!';
            tmpErrors = {
              ...tmpErrors,
              [item.uid]: 'min',
            };
          }
        }
      });

      setErrors(tmpErrors);
    }

    if (errorDescription) {
      alert(errorDescription);
    }

    if (errorDescription) return;

    await add({
      ...product,
      value: product.enable_promotion ? product.value_promotion : product.value,
      quantity,
      observations,
      complements: cartComplements,
    });

    setObservations('');

    onClose();
  }, [
    add,
    quantity,
    product,
    onClose,
    allComplements,
    selectedComplements,
    observations,
  ]);

  const handleSelectComplement = useCallback(
    (item: IComplementItem, group: IComplement) => {
      let total = 0;
      Object.values(selectedComplements).forEach(value => {
        if (value.group === group.uid) total += value.quantity;
      });

      const isFree = total < parseFloat(group.max);

      const tmp = {
        ...selectedComplements,
      } as ISelectComplement;

      if (!tmp[item.uid]) {
        if (isFree) {
          tmp[item.uid] = {
            item,
            quantity: 1,
            group: group.uid,
          };
        }
      } else {
        delete tmp[item.uid];
      }

      setSelectedComplements(tmp);
    },
    [selectedComplements],
  );

  const handleClickPlusComplement = useCallback(
    (item: IComplementItem, group: IComplement) => {
      let total = 0;
      Object.values(selectedComplements).forEach(value => {
        if (value.group === group.uid) total += value.quantity;
      });

      const isFree = total < parseFloat(group.max);

      if (isFree) {
        const tmp = {
          ...selectedComplements,
        } as ISelectComplement;

        tmp[item.uid].quantity += 1;

        setSelectedComplements(tmp);
      }
    },
    [selectedComplements],
  );

  const handleClickMinusComplement = useCallback(
    (item: IComplementItem) => {
      const tmp = {
        ...selectedComplements,
      } as ISelectComplement;

      let qtd = tmp[item.uid].quantity;

      qtd -= 1;

      if (qtd >= 1) {
        tmp[item.uid].quantity = qtd;

        setSelectedComplements(tmp);
      }
    },
    [selectedComplements],
  );

  return (
    <>
      {product.uid && (
        <>
          <div
            className="modal fade show"
            id="addproductcart"
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'block' }}
          >
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content product border-0 shadow-sm">
                <figure
                  className="text-center mb-0"
                  style={{ 
                    background: `${company.app_color}60`,
                    width: '100%',
                    margin: 0,
                    height: '250px'
                  }}
                >
                  <img
                    src={product.image || company.image}
                    alt=""
                    className="w-100 h-100"
                    style={{
                      objectFit: 'cover',
                      borderRadius: '0.375rem 0.375rem 0 0'
                    }}
                  />
                </figure>
                <div className="modal-body">
                  <p className="mb-2">
                    <span 
                      className="text-opac fw-bold" 
                      style={{ 
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {product.category.name}
                    </span>
                    {/* <small className="float-end">
                      <span className="text-opac">4.5</span>{' '}
                      <i className="bi bi-star-fill text-warning" />
                    </small> */}
                  </p>

                  <h5
                    className="text-color-theme mb-2 fw-bold"
                    style={{ 
                      color: company.app_color,
                      fontSize: '20px',
                      lineHeight: '1.2'
                    }}
                  >
                    {product.name}
                  </h5>
                  {product.enable_promotion && (
                    <span
                      className="badge badge-success"
                      style={{
                        background: '#1ee0ac',
                      }}
                    >
                      Promoção
                    </span>
                  )}

                  {product.description && (
                    <p className="fs-14px mb-3">
                      <span className="text-opac" style={{ fontSize: '13px' }}>
                        {product.description}
                      </span>
                    </p>
                  )}

                  <div className="row">
                    <div className="col">
                      <p className="mb-0">
                        <span 
                          className="fw-bold" 
                          style={{ 
                            fontSize: '18px',
                            color: company.app_color
                          }}
                        >
                          {parseFloat(product.value) === 0
                            ? productValue
                            : getCurrencyFormatted(
                                parseFloat(
                                  product.enable_promotion
                                    ? product.value_promotion
                                    : product.value,
                                ),
                              )}
                        </span>

                        {product.enable_promotion && (
                          <span
                            style={{
                              textDecoration: 'line-through',
                              marginLeft: 8,
                              fontSize: 10,
                            }}
                          >
                            {getCurrencyFormatted(parseFloat(product.value))}
                          </span>
                        )}

                        {product.measure === 'KG' && (
                          <>
                            <br />
                            <small className="text-opac">por 1 kg</small>
                          </>
                        )}
                      </p>
                    </div>
                    {product.measure === 'KG' ? (
                      <div className="col-auto">
                        {/* button counter increamenter */}
                        <div className="counter-number">
                          <button
                            type="button"
                            className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                            onClick={handleClickMinusKg}
                            style={{ background: `${company.app_color}60` }}
                          >
                            <i
                              className="bi bi-dash size-22"
                              style={{ color: company.app_color }}
                            />
                          </button>
                          <input
                            type="number"
                            defaultValue={1}
                            value={quantity.toFixed(3)}
                          />
                          <button
                            type="button"
                            className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                            onClick={handleClickPlusKg}
                            style={{ background: `${company.app_color}60` }}
                          >
                            <i
                              className="bi bi-plus size-22"
                              style={{ color: company.app_color }}
                            />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="col-auto">
                        {/* button counter increamenter */}
                        <div className="counter-number">
                          <button
                            type="button"
                            className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                            onClick={handleClickMinus}
                            style={{ background: `${company.app_color}60` }}
                          >
                            <i
                              className="bi bi-dash size-22"
                              style={{ color: company.app_color }}
                            />
                          </button>
                          <span>{quantity}</span>
                          <button
                            type="button"
                            className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                            onClick={handleClickPlus}
                            style={{ background: `${company.app_color}60` }}
                          >
                            <i
                              className="bi bi-plus size-22"
                              style={{ color: company.app_color }}
                            />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {allComplements.map(item => (
                    <div className="row mb-3" key={item.uid}>
                      <div className="col">
                        <div className={errors[item.uid] ? 'isError' : ''}>
                          <h6 className="mb-0">
                            <span className="text-opac small">{item.name}</span>
                          </h6>
                          <p className="fs-small mb-0">
                            <small className="text-danger fs-small">
                              {item.type === '1'
                                ? '* obrigatório'
                                : '* opcional'}
                              <small
                                className="fs-small text-dark text-opac"
                                style={{ marginLeft: 8 }}
                              >
                                {`quant. mínima: ${item.min} | quant. máxima: ${item.max}`}
                              </small>
                            </small>
                          </p>
                        </div>

                        <ul className="list-group complements">
                          {groupItemsByTitle(item.items, item.groupOrders).map(([groupTitle, groupItems]) => (
                            <div key={groupTitle}>
                              {groupTitle !== 'Outros' && (
                                <div className="group-title-container" style={{ marginTop: 16, marginBottom: 8 }}>
                                  <h6 className="group-title" style={{ 
                                    fontSize: 16, 
                                    fontWeight: 600, 
                                    color: company.app_color,
                                    margin: '8px 0 8px 0',
                                    padding: '0',
                                    borderBottom: 'none',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>{groupTitle}</h6>
                                </div>
                              )}
                              {groupItems.map(complement => (
                                <>
                                  {complement.status && (
                                    <li
                                      className="list-group-item"
                                      key={complement.uid}
                                    >
                                      <div
                                        onClick={() => {
                                          handleSelectComplement(complement, item);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <input
                                          className="form-check-input me-2"
                                          type="checkbox"
                                          checked={
                                            selectedComplements[complement.uid] !==
                                            undefined
                                          }
                                        />
                                        <span style={{ 
                                          fontSize: '15px',
                                          fontWeight: 500
                                        }}>
                                          {complement.name}
                                        </span>
                                        {complement.description && (
                                          <>
                                            <br />
                                            <span
                                              className="text-opac"
                                              style={{ fontSize: '11px' }}
                                            >
                                              {complement.description}
                                            </span>
                                          </>
                                        )}
                                        {parseFloat(complement.value) > 0 && (
                                          <>
                                            <br />
                                            <span 
                                              className="aditional-value" 
                                              style={{ 
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#000'
                                              }}
                                            >
                                              {`${complement.value_formatted}`}
                                            </span>
                                          </>
                                        )}
                                      </div>

                                      {selectedComplements[complement.uid] &&
                                        !item.pizzaType && (
                                          <div className="col-auto">
                                            {/* button counter increamenter */}
                                            <div className="counter-number">
                                              <button
                                                type="button"
                                                className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                                                onClick={() => {
                                                  handleClickMinusComplement(
                                                    complement,
                                                  );
                                                }}
                                                style={{
                                                  background: `${company.app_color}60`,
                                                }}
                                              >
                                                <i
                                                  className="bi bi-dash size-22"
                                                  style={{
                                                    color: company.app_color,
                                                  }}
                                                />
                                              </button>
                                              <span>
                                                {
                                                  selectedComplements[
                                                    complement.uid
                                                  ].quantity
                                                }
                                              </span>
                                              <button
                                                type="button"
                                                className="btn btn-sm avatar avatar-30 p-0 rounded-circle"
                                                onClick={() => {
                                                  handleClickPlusComplement(
                                                    complement,
                                                    item,
                                                  );
                                                }}
                                                style={{
                                                  background: `${company.app_color}60`,
                                                }}
                                              >
                                                <i
                                                  className="bi bi-plus size-22"
                                                  style={{
                                                    color: company.app_color,
                                                  }}
                                                />
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                    </li>
                                  )}
                                </>
                              ))}
                              {groupTitle !== 'Outros' && <div className="mb-3" />}
                            </div>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}

                  <div className="form-group">
                    <label style={{ fontSize: 12 }}>Observações</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Digite aqui alguma observação..."
                      style={{ border: '1px solid #ced4da', fontSize: 12 }}
                      onChange={event => {
                        setObservations(event.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="modal-footer justify-content-center">
                  <button
                    type="button"
                    className="btn btn-link text-color-theme"
                    data-bs-dismiss="modal"
                    onClick={handleClickAdd}
                    style={{ color: company.app_color }}
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>
            <div
              className="modal-backdrop fade show"
              style={{ background: '#00000060' }}
              onClick={() => onClose()}
            />
          </div>
        </>
      )}
    </>
  );
};

export default AddProduct;
